import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, query, queryAll, property } from 'lit/decorators.js';
import componentStyles from './se-profile-nav.css.lit';
import { MobileController } from '../../controllers/mobile-controller';
import { SlotController } from '../../controllers/slot-controller';

import '../se-button/se-button';
import '../../base/outline-icon/outline-icon';

/**
 * The SeProfileNav component
 * @element se-profile-nav
 */
@customElement('se-profile-nav')
export class SeProfileNav extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(this, true);
  private mobileController = new MobileController(this);

  @query('.profile-menu--nav')
  nav: HTMLElement;

  @query('.profile-menu--button')
  button: HTMLElement;

  @queryAll('profile-menu--link')
  links: HTMLElement[];

  @property({ type: Boolean })
  isMobile = this.mobileController.isMobile;

  @property({ type: Boolean })
  mobileOpen = false;

  // provides unique id number to pass to elements.
  seed: string = Math.floor(Math.random() * 10000).toString();

  ul: HTMLElement;

  lis: HTMLElement[];

  anchors: HTMLElement[];

  activeID: string;

  mobileButtonText: string | undefined;

  render(): TemplateResult {
    return html`
      <section class="profile-menu">
        <se-button
          @click="${this.toggleMobileOpen}"
          variant="dropdown"
          class="profile-menu--button"
          ?hidden="${!this.isMobile}"
          aria-expanded="${this.mobileOpen}"
          aria-controls="nav-${this.seed}"
          aria-haspopup="true"
          button-label="profile menu"
        >
          ${this.setButtonIcon()}
        </se-button>
        <nav
          class="profile-menu--nav closed hidden"
          aria-label="profile-nav"
          id="nav-${this.seed}"
        >
          <slot></slot>
        </nav>
      </section>
    `;
  }

  firstUpdated() {
    this.initializeSlottedMarkup();
    this.setAccessibilityAttributes();
    window.addEventListener('resize', this.toggleOnMobile);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.toggleOnMobile);
    this.removeEventHandlers();
  }

  initializeSlottedMarkup() {
    this.getElements();
    this.setMobileButtonText();
    this.setClassNamesAndIDs();
    this.setInitialMobileState();
    this.addEventHandlers();
  }

  setAccessibilityAttributes() {
    this.getActiveID();
    this.setAriaCurrent();
    this.setParentActiveClass();
  }

  getElements() {
    const UL = [...this.children].filter(
      child => child.localName === 'ul'
    )[0] as HTMLElement;
    const LIs = [...UL.children].filter(
      child => child.localName === 'li'
    ) as HTMLElement[];
    const As = [...LIs].map(
      el => [...el.children].filter(child => child.localName === 'a')[0]
    ) as HTMLElement[];
    this.ul = UL;
    this.lis = LIs;
    this.anchors = As;
  }

  setMobileButtonText() {
    const text = this.anchors.filter(a => a.classList.contains('active'))[0]
      ?.innerText;
    if (text) {
      this.shadowRoot?.querySelector('.profile-menu--button')?.append(text);
    }
    if (!text) {
      this.shadowRoot
        ?.querySelector('.profile-menu--button')
        ?.append(this.anchors[0]?.innerText);
    }
  }

  setClassNamesAndIDs() {
    this.ul.classList.add('profile-menu--list');
    this.lis.forEach((li, i) => {
      li.classList.add('profile-menu--item');
      li.setAttribute('id', `item-${this.seed}-${i}`);
    });
    this.anchors.forEach((a, i) => {
      a.innerHTML = `<span>${a.innerHTML}</span>`;
      a.classList.add('profile-menu--link');
      a.setAttribute('id', `link-${this.seed}-${i}`);
    });
  }

  setInitialMobileState() {
    this.isMobile = this.mobileController.isMobile;
    this.setMobileClasses();
  }

  addEventHandlers() {
    this.anchors.forEach(a =>
      a.addEventListener('keydown', this.handleKeyboardNav)
    );
    this.lis.forEach(li => li.addEventListener('click', this.handleLinkClick));
  }

  removeEventHandlers() {
    this.anchors.forEach(a =>
      a.removeEventListener('keydown', this.handleKeyboardNav)
    );
    this.lis.forEach(li =>
      li.removeEventListener('click', this.handleLinkClick)
    );
  }

  getActiveID() {
    const currentlyActive = this.anchors.filter(a =>
      a.classList.contains('active')
    ).length
      ? this.anchors.filter(a => a.classList.contains('active'))[0].id
      : false;

    if (currentlyActive) {
      this.activeID = currentlyActive;
    } else {
      const firstAnchor = this.anchors[0] as HTMLAnchorElement;
      this.activeID = firstAnchor.id;
      this.setActiveOverride(firstAnchor);
    }
  }

  setAriaCurrent() {
    document
      .querySelector(`#${this.activeID}`)
      ?.setAttribute('aria-current', 'page');
  }

  setParentActiveClass() {
    document
      .querySelector(`#${this.activeID}`)
      ?.parentElement?.classList.add('active-item');
  }

  toggleOnMobile() {
    const component = document.querySelector('se-profile-nav') as SeProfileNav;
    const hasChanged =
      component.isMobile !== component.mobileController.isMobile;
    if (hasChanged) {
      component.isMobile = !component.isMobile;
      component.setMobileClasses();
    }
  }

  setMobileClasses() {
    const listElements = [...this.lis, ...this.anchors, this.ul, this.nav];
    if (!this.isMobile) {
      [...listElements].forEach(el => {
        if (el.classList.contains('mobile')) {
          el.classList.remove('mobile');
        }
      });
    }

    if (this.isMobile) {
      [...listElements].forEach(el => {
        if (!el.classList.contains('mobile')) {
          el.classList.add('mobile');
        }
      });
      this.nav.classList.add('closed');
      this.nav.classList.add('hidden');
    }
  }

  handleKeyboardNav(event: KeyboardEvent) {
    let index = 0;
    const links = document
      .querySelector('se-profile-nav')
      ?.shadowRoot?.querySelectorAll('.profile-menu--link');
    const start = Array.prototype.indexOf.call(
      links,
      event.target as HTMLElement
    );

    // bail on wrong key
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    if (event.key === 'ArrowDown') {
      if (links?.length && start + 1 > links?.length - 1) {
        index = 0;
      } else index = start + 1;
    }
    if (event.key === 'ArrowUp') {
      if (links?.length && start - 1 < 0) {
        index = links?.length - 1;
      } else index = start - 1;
    }
    const focusTarget = links?.[index] as HTMLElement;

    focusTarget.focus();
  }

  handleLinkClick() {
    (
      [...this.children]?.filter(
        child => child.localName === 'a'
      )[0] as HTMLAnchorElement
    ).click();
  }

  setActiveOverride(el: HTMLAnchorElement) {
    el.classList.add('active');
  }

  toggleHidden() {
    this.nav.classList.add('hidden');
  }

  toggleMobileOpen() {
    if (this.isMobile) {
      if (this.nav.classList.contains('open')) {
        this.nav.classList.remove('open');
        this.nav.classList.add('closed');
        setTimeout(() => this.toggleHidden(), 500);
        return (this.mobileOpen = false);
      } else {
        this.nav.classList.remove('closed');
        this.nav.classList.remove('hidden');
        this.nav.classList.add('open');
        return (this.mobileOpen = true);
      }
    } else {
      // bail
      return;
    }
  }

  setButtonIcon() {
    if (this.mobileOpen) {
      return html` <outline-icon
        slot="right"
        size="1em"
        name="chevron-up"
        library="system"
        color="brand-primary-2"
      ></outline-icon>`;
    }
    return html` <outline-icon
      slot="right"
      size="1em"
      name="chevron-down"
      library="system"
      color="brand-primary-2"
    ></outline-icon>`;
  }
}
